/**
 * ATU2 2.0 검증 상태(Fable 안) 페이지 전용 read-only API 클라이언트.
 *
 * GET 엔드포인트만 사용한다. 상태를 바꾸는 POST(run, model-rerun-audit 등)는
 * 이 파일에 정의하지 않는 것으로 read-only 안전장치를 코드 수준에서 보장한다.
 */
import { apiFetch } from '../utils/apiFetch';
import {
  fetchDailyTacticalImprovementProposals,
  fetchDailyTacticalOpsReportHistory,
} from './operationsApi';
import type {
  DailyTacticalBlockerAlert,
  DailyTacticalOpsReport,
  DailyTacticalImprovementProposals,
  DailyTacticalReportHistory,
  DailyTacticalReadiness,
  DailyTacticalWeeklyReview,
  MarketStatusSimple,
  OperationsOverview,
} from './operationsApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export interface DtsRecentRun {
  id: string;
  trading_date: string;
  status: string;
  cohort_id: string;
  protocol_version: string;
  created_at: string | null;
}

export interface DtsSummary {
  mode: string;
  stage: string;
  scheduler_safe_default: string;
  broker_order_submit_count: number;
  real_capital: number;
  recent_runs: DtsRecentRun[];
}

export interface DtsAuditSection {
  implemented: boolean;
  passed: boolean;
  rows: unknown[];
  note?: string;
}

export interface DtsWeeklyAudit {
  mode: string;
  stage: string;
  cohort_id: string;
  sample_run_count: number;
  broker_order_submit_count: number;
  real_capital: number;
  live_broker_orders_count: number;
  storage_integrity_audit: DtsAuditSection;
  model_rerun_audit: DtsAuditSection;
  fact_check_audit: DtsAuditSection;
}

async function getJson<T>(path: string, label: string): Promise<T> {
  const response = await apiFetch(`${API_BASE_URL}${path}`);
  if (!response.ok) {
    throw new Error(`${label} 조회 실패 (HTTP ${response.status})`);
  }
  return response.json();
}

export function fetchFableReadiness(limit = 20): Promise<DailyTacticalReadiness> {
  return getJson(
    `/api/operations/daily-tactical-shadow/readiness?limit=${limit}`,
    '시작 전 점검(readiness)',
  );
}

export function fetchFableSummary(limit = 5): Promise<DtsSummary> {
  return getJson(
    `/api/operations/daily-tactical-shadow?limit=${limit}`,
    '최근 실행 기록',
  );
}

export function fetchFableWeeklyAudit(cohortId: string): Promise<DtsWeeklyAudit> {
  return getJson(
    `/api/operations/daily-tactical-shadow/weekly-audit?cohort_id=${encodeURIComponent(cohortId)}`,
    '주간 감사(weekly audit)',
  );
}

export function fetchFableOpsReport(cohortId: string): Promise<DailyTacticalOpsReport> {
  return getJson(
    `/api/operations/daily-tactical-shadow/ops-report?cohort_id=${encodeURIComponent(cohortId)}`,
    '자동 운영 리포트(ops report)',
  );
}

export function fetchFableWeeklyReview(cohortId: string): Promise<DailyTacticalWeeklyReview> {
  return getJson(
    `/api/operations/daily-tactical-shadow/weekly-review?cohort_id=${encodeURIComponent(cohortId)}`,
    '주간 운영 리뷰(weekly review)',
  );
}

export function fetchFableBlockerAlert(cohortId: string): Promise<DailyTacticalBlockerAlert> {
  return getJson(
    `/api/operations/daily-tactical-shadow/blocker-alert?cohort_id=${encodeURIComponent(cohortId)}`,
    '자동 운영 blocker 알림',
  );
}

export function fetchFableOpsReportHistory(
  cohortId: string,
  limit = 20,
): Promise<DailyTacticalReportHistory> {
  return fetchDailyTacticalOpsReportHistory(cohortId, limit);
}

export function fetchFableImprovementProposals(
  cohortId: string,
): Promise<DailyTacticalImprovementProposals> {
  return fetchDailyTacticalImprovementProposals(cohortId);
}

export function fetchFableOverview(): Promise<OperationsOverview> {
  return getJson('/api/operations/overview?hours=24&limit=5', '운영 상태(overview)');
}

export function fetchFableMarketFallback(): Promise<MarketStatusSimple> {
  return getJson('/api/market/status', '시장 상태');
}
